<template>
  <div class="tdp">
    <el-select
      v-model="day"
      placeholder="วัน"
      :disabled="disabled"
      class="tdp-day"
      filterable
    >
      <el-option
        v-for="d in daysInSelectedMonth"
        :key="d"
        :value="d"
        :label="String(d)"
      />
    </el-select>
    <el-select
      v-model="month"
      placeholder="เดือน"
      :disabled="disabled"
      class="tdp-month"
      filterable
    >
      <el-option
        v-for="(m, i) in MONTHS_TH"
        :key="i + 1"
        :value="i + 1"
        :label="m"
      />
    </el-select>
    <el-select
      v-model="yearBe"
      placeholder="ปี"
      :disabled="disabled"
      class="tdp-year"
      filterable
    >
      <el-option
        v-for="y in yearOptions"
        :key="y"
        :value="y"
        :label="String(y)"
      />
    </el-select>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

/**
 * Thai-style date picker: three dropdowns (day, month, year-in-พ.ศ.) instead of
 * a calendar pop-up. Designed for older customers on mobile who find the
 * el-date-picker calendar fiddly.
 *
 *   v-model receives / emits a YYYY-MM-DD Gregorian string (matches the rest
 *   of the form). The user-facing year is Buddhist (พ.ศ. = ค.ศ. + 543).
 *
 *   yearSpan defaults to 110 years backward so we cover infants to centenarians.
 *   Set `futureYears` if booking a future date is allowed (e.g. registerDate).
 */
const props = withDefaults(
  defineProps<{
    modelValue: string;
    disabled?: boolean;
    /** How many years backwards from THIS year to show. Default 110. */
    yearSpan?: number;
    /** How many years into the future to allow. Default 0 (birthday case). */
    futureYears?: number;
  }>(),
  { disabled: false, yearSpan: 110, futureYears: 0 },
);

const emit = defineEmits<{
  "update:modelValue": [string];
}>();

const MONTHS_TH = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const day = ref<number | undefined>(undefined);
const month = ref<number | undefined>(undefined);
const yearBe = ref<number | undefined>(undefined);

// Buddhist-era years, latest first so the most recent year is at the top.
const yearOptions = computed<number[]>(() => {
  const nowBe = new Date().getFullYear() + 543;
  const start = nowBe + props.futureYears;
  const end = start - (props.yearSpan + props.futureYears) + 1;
  const out: number[] = [];
  for (let y = start; y >= end; y--) out.push(y);
  return out;
});

// Days in the currently selected month — falls back to 31 when month/year
// hasn't been chosen so the picker is responsive immediately.
const daysInSelectedMonth = computed<number[]>(() => {
  const m = month.value;
  const y = yearBe.value !== undefined ? yearBe.value - 543 : undefined;
  if (m === undefined || y === undefined) {
    return Array.from({ length: 31 }, (_, i) => i + 1);
  }
  // Day 0 of next month = last day of current month.
  const lastDay = new Date(y, m, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) => i + 1);
});

// Hydrate from props.modelValue ("YYYY-MM-DD" Gregorian).
function hydrate(v: string): void {
  if (!v) {
    day.value = undefined;
    month.value = undefined;
    yearBe.value = undefined;
    return;
  }
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return;
  yearBe.value = Number(m[1]) + 543;
  month.value = Number(m[2]);
  day.value = Number(m[3]);
}

hydrate(props.modelValue);

// Re-hydrate if the parent swaps the value (e.g. draft restoration).
watch(
  () => props.modelValue,
  (v) => {
    // Only re-hydrate when the inbound value disagrees with the current
    // internal state to avoid stomping mid-edit.
    const current = composeIso();
    if (v !== current) hydrate(v);
  },
);

// Auto-correct day when month/year change makes the current day invalid
// (e.g. switching from 31 March → February).
watch([month, yearBe], () => {
  if (day.value === undefined) return;
  const maxDay = daysInSelectedMonth.value.length;
  if (day.value > maxDay) day.value = maxDay;
});

// Emit when all three are set; emit '' if any is missing.
function composeIso(): string {
  if (
    day.value === undefined ||
    month.value === undefined ||
    yearBe.value === undefined
  ) {
    return "";
  }
  const y = yearBe.value - 543;
  const mm = String(month.value).padStart(2, "0");
  const dd = String(day.value).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

watch([day, month, yearBe], () => {
  emit("update:modelValue", composeIso());
});
</script>

<style scoped>
.tdp {
  display: grid;
  grid-template-columns: 80px 1fr 110px;
  gap: 6px;
  width: 100%;
}
.tdp :deep(.el-select__wrapper) {
  /* Keep the dropdowns at the same 16-px input size as the rest of the form
     so iOS Safari doesn't zoom on focus (customer.css enforces this on the
     register shell at the outer scope, but explicit here is harmless). */
  font-size: 16px;
}
@media (max-width: 380px) {
  .tdp {
    grid-template-columns: 68px 1fr 96px;
    gap: 4px;
  }
}
</style>
